import Select from 'components/Select';
import { IOption } from 'components/Select/Select';
import React, { useEffect, useRef, useState } from 'react';
import DatePicker, { DateObject, DatePickerRef } from 'react-multi-date-picker';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';
import gregorian from 'react-date-object/calendars/gregorian';
import gregorian_en from 'react-date-object/locales/gregorian_en';
import TimePicker from 'react-multi-date-picker/plugins/time_picker';
import DatePanel from 'react-multi-date-picker/plugins/date_panel';
import Button from 'components/Button/Button';

import {
  DateContainer,
  DateSpan,
} from 'containers/Tasks/TasksManagement/styles';
import { smartDateDuration, smartDateString } from 'utils/date';
import { numberToTime } from 'utils/number';
import './style.css';
import Input from 'components/Input';
import i18n, { languageDirection } from '../../i18n';
import { getLanguageBaseOnTZ } from '../../utils/LanguageLocalStorage';
import { ILocal } from '../Chart/BaseDatePicker';
import { useHolidays } from '../../context/HolidayProvider/HolidayProvider';
import moment from 'moment-jalaali';

export interface IMultiDatePicker_Range {
  isEditable?: boolean;
  hasTime?: boolean;
  label?: string;
  id?: string;
  minDate?: number;
  maxDate?: number;
  className?: string;
  placeholder?: string;
  values?: {
    startDate?: number;
    dueDate?: number;
  };
  setDate: (values: {
    startDate?: number | null;
    dueDate?: number | null;
  }) => void;
}

export type IMultiDatePicker = {
  isEditable?: boolean;
  label?: string | React.ReactNode;
  placeholder?: string;
  id?: string;
  minDate?: number;
  maxDate?: number;
  className?: string;
  style?: any;
  inputStyle?: any;
  required?: boolean;
} & (
  | {
      value?: number;
      isWithDuration?: undefined;
      hasTime?: boolean;
      setDate: (value: number | null) => void;
    }
  | {
      value?: {
        startDate?: number;
        dueDate?: number;
      };
      isWithDuration: true;
      hasTime: true;
      setDate: (value: {
        startDate: number | null;
        dueDate: number | null;
      }) => void;
    }
);

export const getMultiDateLanguageConfiguration = () => {
  const lang = getLanguageBaseOnTZ();
  switch (lang) {
    case 'en':
      return {
        calendar: gregorian,
        locale: gregorian_en,
      };
    case 'fa':
      return {
        calendar: persian,
        locale: persian_fa,
      };
    default:
      return {
        calendar: persian,
        locale: persian_fa,
      };
  }
};

export const getMultiDateLanguageConfigurationBaseOnLang = (
  lang: ILocal | undefined
) => {
  switch (lang) {
    case 'en':
      return {
        calendar: gregorian,
        locale: gregorian_en,
      };
    case 'fa_IR@calendar=persian':
      return {
        calendar: persian,
        locale: persian_fa,
      };
    default:
      return {
        calendar: persian,
        locale: persian_fa,
      };
  }
};

export const MultiDatePicker = (props: IMultiDatePicker) => {
  const {
    isEditable = true,
    hasTime,
    isWithDuration,
    label,
    id,
    minDate,
    maxDate,
    value,
    setDate,
    className,
    style,
    inputStyle,
    required = false,
  } = props;
  const [pickedDate, setPickedDate] = useState<DateObject | null>(null);
  const [duration, setDuration] = useState(0);
  const datePickerRef = useRef<DatePickerRef>();
  const holidaysCntx = useHolidays();

  const gap = 15;
  const durationPickerOptions = [...Array(21).keys()].map((it) => {
    return {
      value: it * gap,
      label: numberToTime(it * gap),
    };
  });

  function onSubmit(e: any) {
    e.stopPropagation();
    if (!pickedDate) return;
    if (isWithDuration) {
      const submitFunction = setDate as (value: {
        startDate: number;
        dueDate: number;
      }) => void;

      const endTemp = pickedDate;
      submitFunction({
        startDate: pickedDate.setSecond(0).valueOf(),
        dueDate: endTemp
          .setHour(pickedDate?.hour)
          .setMinute(pickedDate?.minute + duration)
          .setSecond(0)
          .valueOf(),
      });
    } else {
      const submitFunction = setDate as (value: number) => void;
      let res = pickedDate;
      if (!hasTime) res = res.setHour(0).setMinute(0);
      submitFunction(res.setSecond(0).valueOf());
    }
    setPickedDate(null);
    datePickerRef.current?.closeCalendar?.();
  }

  function onReset() {
    if (isWithDuration) {
      const submitFunction = setDate as (value: {
        startDate: number | null;
        dueDate: number | null;
      }) => void;

      submitFunction({
        startDate: null,
        dueDate: null,
      });
    } else {
      const submitFunction = setDate as (value: number | null) => void;
      submitFunction(null);
    }
    setPickedDate(null);
    datePickerRef.current?.closeCalendar?.();
  }

  function onChangeDuration(value: Array<IOption>) {
    setDuration(Number(value[0].value));
  }

  useEffect(() => {
    if (isWithDuration && value?.dueDate && value?.startDate) {
      setDuration(() => (value.dueDate! - value.startDate!) / (1000 * 60));
    }

    if (value && typeof value === 'object' && value.startDate) {
      setPickedDate(new DateObject(value.startDate));
    } else if (typeof value !== 'object' && value) {
      setPickedDate(new DateObject(value));
    } else {
      const now = new DateObject();
      now.setMinute(Math.ceil(now.minute / 5) * 5);
      setPickedDate(now);
    }
  }, [value]);

  const conf = getMultiDateLanguageConfiguration();

  return (
    <div
      style={style}
      className={hasTime ? 'multi-date-picker with-time' : 'multi-date-picker'}
    >
      <DateContainer>
        <DateSpan clickable={isEditable}>
          <DatePicker
            className={className}
            id={id}
            ref={datePickerRef}
            calendar={conf.calendar}
            locale={conf.locale}
            format={hasTime ? 'YYYY/MM/DD HH:mm' : 'YYYY/MM/DD'}
            value={
              pickedDate &&
              moment(pickedDate.toDate()).jYear().toString() ===
                holidaysCntx.selectedYear
                ? new DateObject(pickedDate)
                : undefined
            }
            disabled={!isEditable}
            readOnly={!isEditable}
            placeholder={
              props.placeholder ||
              (i18n.t('withoutDate', { ns: 'taskManager' }) as string)
            }
            calendarPosition={'top'}
            arrow={false}
            portal={true}
            zIndex={100000000}
            minDate={minDate}
            maxDate={maxDate}
            mapDays={({ date }) => {
              const chosenDate = new DateObject(date);
              const chosenDateTimestamp = moment(chosenDate.toDate().getTime());
              if (
                chosenDateTimestamp.format('jYYYY') !==
                holidaysCntx.selectedYear
              )
                holidaysCntx.handleChangeSelectedYear(
                  chosenDateTimestamp.format('jYYYY')
                );
              if (
                holidaysCntx.holidays &&
                holidaysCntx.holidays.includes(
                  chosenDateTimestamp.format('jYYYY/jMM/jDD')
                )
              ) {
                return {
                  style: { color: 'red' },
                };
              }
              return {};
            }}
            render={(_, openCalendar) => {
              return (
                <Input
                  disabled={!isEditable}
                  id={`${id}_inputview`}
                  label={label}
                  type="text"
                  style={{
                    width: '100%',
                    cursor: isEditable ? 'pointer' : 'default',
                    ...inputStyle,
                  }}
                  wrapperStyle={{ width: '100%' }}
                  onClick={openCalendar}
                  placeholder={props.placeholder}
                  required={required}
                  value={
                    isWithDuration
                      ? value?.startDate && value?.dueDate
                        ? smartDateDuration(
                            new DateObject(value.startDate).valueOf(),
                            new DateObject(value.dueDate).valueOf(),
                            hasTime
                          )
                        : required
                          ? null
                          : (i18n.t('withoutDate', {
                              ns: 'taskManager',
                            }) as string)
                      : value
                        ? smartDateString(
                            new DateObject(value).setSecond(0).valueOf(),
                            hasTime
                          )
                        : required
                          ? null
                          : (i18n.t('withoutDate', {
                              ns: 'taskManager',
                            }) as string)
                  }
                />
              );
            }}
            plugins={[
              ...(hasTime
                ? [
                    // eslint-disable-next-line react/jsx-key
                    <TimePicker
                      hideSeconds={true}
                      mStep={5}
                      position="bottom"
                      style={{
                        borderBottom: '1px solid #cfd8e2',
                        width: '100%',
                      }}
                    />,
                  ]
                : []),
            ]}
            onChange={(dateObjects) => {
              setPickedDate(dateObjects as DateObject);
              datePickerRef.current?.openCalendar?.();
            }}
          >
            <div className="flex flex-col gap-4">
              {isWithDuration && hasTime && (
                <Select
                  allowRemove={false}
                  containerClass={`duration-dropdown ${languageDirection() === 'ltr' ? 'duration-dropdown-ltr' : 'duration-dropdown-rtl'}`}
                  onChangeValues={onChangeDuration}
                  containerStyle={{
                    borderBottom: '1px solid #cfd8e2',
                    direction: languageDirection(),
                  }}
                  inputStyle={{
                    margin: '16px auto',
                    width: '80%',
                    direction: languageDirection(),
                  }}
                  values={[
                    durationPickerOptions.find(
                      (it) => Math.abs(it.value - duration) <= gap / 2
                    )!,
                  ]}
                  options={durationPickerOptions}
                  label={i18n.t('setTime', { ns: 'taskManager' }) as string}
                />
              )}
              <div className="flex justify-evenly items-center py-4">
                <Button
                  onClick={onSubmit}
                  type="button"
                  className="w-32"
                  color="green"
                >
                  {i18n.t('setTime', { ns: 'taskManager' }) as string}
                </Button>
                <Button
                  onClick={onReset}
                  type="button"
                  className="w-32"
                  color="red"
                >
                  {i18n.t('removeDate', { ns: 'taskManager' }) as string}
                </Button>
              </div>
            </div>
          </DatePicker>
        </DateSpan>
      </DateContainer>
    </div>
  );
};

export const MultiDatePicker_Range = (props: IMultiDatePicker_Range) => {
  const {
    isEditable = true,
    hasTime,
    label,
    minDate,
    maxDate,
    values,
    id,
    className,
    setDate,
  } = props;
  const [dates, setDates] = useState<DateObject[]>([]);
  const datePickerRef = useRef<DatePickerRef>();
  const holidaysCntx = useHolidays();

  function onSubmit() {
    const sDate = hasTime
      ? dates?.[0]
      : dates?.[0].setHour(0).setMinute(0).setSecond(0);
    const dDate = hasTime
      ? dates?.[1]
      : dates?.[1]?.setHour(23)?.setMinute(59)?.setSecond(59);
    setDate({
      startDate: sDate?.valueOf() || 0,
      dueDate: dDate?.valueOf() || 0,
    });
    datePickerRef?.current?.closeCalendar?.();
  }

  function onReset() {
    setDate({
      startDate: null,
      dueDate: null,
    });
    setDates([]);
    datePickerRef?.current?.closeCalendar?.();
  }

  useEffect(() => {
    if (values?.startDate) {
      setDates([new DateObject().setDate(values.startDate!)]);
    }
    if (values?.dueDate) {
      setDates((prev) => {
        prev![1] = new DateObject().setDate(values.dueDate!);
        return prev;
      });
    }
  }, [values]);
  const conf = getMultiDateLanguageConfiguration();

  return (
    <div className={hasTime ? 'with-time' : ''}>
      <DateContainer>
        <DateSpan clickable={isEditable}>
          <DatePicker
            className={className}
            id={id}
            ref={datePickerRef}
            calendar={conf.calendar}
            locale={conf.locale}
            format={hasTime ? 'YYYY/MM/DD HH:mm' : 'YYYY/MM/DD'}
            range
            rangeHover
            value={dates}
            portal={true}
            zIndex={100000000}
            disabled={!isEditable}
            readOnly={!isEditable}
            placeholder={
              props.placeholder || i18n.t('withoutDate', { ns: 'taskManager' })
            }
            calendarPosition={'top'}
            arrow={false}
            minDate={minDate}
            maxDate={maxDate}
            mapDays={({ date }) => {
              const chosenDate = new DateObject(date);
              const chosenDateTimestamp = moment(chosenDate.toDate().getTime());
              if (
                chosenDateTimestamp.format('jYYYY') !==
                holidaysCntx.selectedYear
              )
                holidaysCntx.handleChangeSelectedYear(
                  chosenDateTimestamp.format('jYYYY')
                );
              if (
                holidaysCntx.holidays &&
                holidaysCntx.holidays.includes(
                  chosenDateTimestamp.format('jYYYY/jMM/jDD')
                )
              ) {
                return {
                  style: { color: 'red' },
                };
              }
              return {};
            }}
            render={(_, openCalendar) => {
              return (
                <Input
                  id={`${id}_inputview`}
                  label={label}
                  type="text"
                  style={{
                    width: '100%',
                    cursor: isEditable ? 'pointer' : 'default',
                  }}
                  wrapperStyle={{ width: '100%' }}
                  onClick={openCalendar}
                  placeholder={props.placeholder}
                  value={
                    values?.startDate || values?.dueDate
                      ? smartDateDuration(
                          values.startDate,
                          values.dueDate,
                          hasTime
                        )
                      : i18n.t('withoutDate', { ns: 'taskManager' })
                  }
                />
              );
            }}
            plugins={[
              ...(hasTime
                ? [
                    // eslint-disable-next-line react/jsx-key
                    <TimePicker
                      style={{
                        borderBottom: '1px solid #cfd8e2',
                        width: '100%',
                      }}
                      hideSeconds={true}
                      mStep={5}
                      position="bottom"
                    />,
                  ]
                : []),
              // eslint-disable-next-line react/jsx-key
              <DatePanel
                position={languageDirection() === 'rtl' ? 'right' : 'left'}
                markFocused
              />,
            ]}
            onChange={(dateObjects) => {
              setDates(dateObjects);
            }}
          >
            <div className="flex justify-between  items-center py-4">
              <Button onClick={onSubmit} className="w-32" color="green">
                {i18n.t('setTime', { ns: 'taskManager' }) as string}
              </Button>
              <Button onClick={onReset} className="w-32" color="red">
                {i18n.t('removeDate', { ns: 'taskManager' }) as string}
              </Button>
            </div>
          </DatePicker>
        </DateSpan>
      </DateContainer>
    </div>
  );
};
